import React, { createContext, useContext, useCallback, useEffect, useState, useMemo } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useAuth } from '@/contexts/AuthContext';

/** Map of section ID to expanded boolean */
type ExpandedSections = Record<string, boolean>;

interface SidebarContextType {
  isMobileOpen: boolean;
  toggleMobile: () => void;
  closeMobile: () => void;

  // Section expand/collapse (V2 collapsible sections)
  expandedSections: ExpandedSections;
  toggleSection: (sectionId: string) => void;
  expandSection: (sectionId: string) => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

const NAV_SECTIONS_KEY_PREFIX = 'skystone_nav_sections_';

function getNavSectionsKey(userId: string | undefined): string {
  return `${NAV_SECTIONS_KEY_PREFIX}${userId ?? 'anonymous'}`;
}

export function SidebarProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const { user } = useAuth();
  const navSectionsKey = getNavSectionsKey(user?.id);

  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Section expand/collapse state
  // Empty object means "no stored state" — auto-expand logic will handle initial state
  const [expandedSections, setExpandedSections] = useLocalStorage<ExpandedSections>(
    navSectionsKey,
    {}
  );

  const toggleMobile = useCallback(() => {
    setIsMobileOpen((prev) => !prev);
  }, []);

  const closeMobile = useCallback(() => {
    setIsMobileOpen(false);
  }, []);

  const toggleSection = useCallback(
    (sectionId: string) => {
      setExpandedSections((prev) => ({
        ...prev,
        [sectionId]: !prev[sectionId],
      }));
    },
    [setExpandedSections]
  );

  const expandSection = useCallback(
    (sectionId: string) => {
      setExpandedSections((prev) => {
        if (prev[sectionId]) return prev; // Already expanded, no update needed
        return { ...prev, [sectionId]: true };
      });
    },
    [setExpandedSections]
  );

  // Close mobile sidebar on Escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMobileOpen) {
        setIsMobileOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMobileOpen]);

  const value = useMemo(
    () => ({
      isMobileOpen,
      toggleMobile,
      closeMobile,
      expandedSections,
      toggleSection,
      expandSection,
    }),
    [
      isMobileOpen,
      toggleMobile,
      closeMobile,
      expandedSections,
      toggleSection,
      expandSection,
    ]
  );

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
}
